import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createLoyaltyMutation } from '~/services/loyalty/lib';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import { useRouter } from 'next/router';
import { extractRewardsStatusByBarcode } from '~/services/loyalty/lib';
import { PaymentMethod } from '~/shared/constants';
import { QueryKeys, MutationKeys } from '~/services/loyalty/constants';
import { Basket } from '~/lib/data-contract/salesforce';
import { useFrame } from '~/shared/utils';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';

export const useReserveMemberReward = () => {
    const { businessCentralId } = useCustomer();
    const { translate } = useTranslation();
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: frame } = useFrame();

    type ReserveError = Error & { reserved?: string[] };

    return useMutation({
        mutationKey: [MutationKeys.RESERVE_REWARD],
        mutationFn: async (paymentInstruments: Basket['paymentInstruments']) => {
            const rewards = extractRewardsStatusByBarcode(
                paymentInstruments,
                PaymentMethod.LOYALTY,
            );

            if (rewards.length === 0) {
                return { reserved: [] };
            }

            const reserved: string[] = [];

            for (const reward of rewards) {
                try {
                    await createLoyaltyMutation('ReserveMemberReward', translate, trackError, {
                        memberId: businessCentralId,
                        rewardCode: reward.barcode,
                    });

                    reserved.push(reward.barcode);
                } catch (err) {
                    throw { err, reserved };
                }
            }

            return { reserved };
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.ACTIVE_MEMBER_REWARDS],
            });
        },
        onError: async (errorWithContext: ReserveError) => {
            // Roll back the successfully reserved rewards on Error
            const reserved: string[] = errorWithContext?.reserved ?? [];

            if (reserved.length) {
                for (const barcode of reserved) {
                    try {
                        await createLoyaltyMutation('ReleaseMemberReward', translate, trackError, {
                            memberId: businessCentralId,
                            rewardCode: barcode,
                        });
                    } catch {
                        // we are just trying to clean up
                    }
                }
            }

            const basketUrl = frame?.settings?.pageReferences?.basketPage?.url ?? '/';
            router.push(basketUrl);
        },
    });
};
